import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Download,
  FileAudio,
  FileText,
  File as FileIcon,
  FolderOpen,
  Play,
} from 'lucide-react';
import { useEffect, useRef, type CSSProperties } from 'react';

import { usePlayer } from '@/contexts/PlayerContext';
import { Button } from '@/components/ui/Button';
import { EmptyState, ErrorState } from '@/components/ui/States';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tooltip } from '@/components/ui/Tooltip';
import { ru } from '@/i18n/ru';
import { cn } from '@/lib/cn';
import {
  downloadUrl,
  extensionLabel,
  formatDurationSafe,
  formatSizeSafe,
  isAudioItem,
  isTextItem,
  splitHighlight,
  type LibraryItem,
} from '@/lib/format';

interface FileListProps {
  items: LibraryItem[];
  highlightQuery?: string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onOpenText: (item: LibraryItem) => void;
  onRevealInFolder?: (item: LibraryItem) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  highlightFileId?: string | null;
}

function FileTypeIcon({ type }: { type: string }) {
  if (type === 'AUDIO') return <FileAudio className="h-3.5 w-3.5 text-accent" />;
  if (type === 'TEXT') return <FileText className="h-3.5 w-3.5 text-text-muted" />;
  return <FileIcon className="h-3.5 w-3.5 text-text-muted" />;
}

function HighlightedName({ name, query }: { name: string; query?: string }) {
  if (!query?.trim()) return <span className="truncate">{name}</span>;
  return (
    <span className="truncate">
      {splitHighlight(name, query).map((part, i) =>
        part.match ? (
          <mark key={i} className="highlight-match bg-transparent p-0">
            {part.text}
          </mark>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </span>
  );
}

function FileRow({
  item,
  highlightQuery,
  playing,
  highlighted,
  onOpenText,
  onPlay,
  onRevealInFolder,
  style,
  className,
}: {
  item: LibraryItem;
  highlightQuery?: string;
  playing: boolean;
  highlighted?: boolean;
  onOpenText: (item: LibraryItem) => void;
  onPlay: (item: LibraryItem) => void;
  onRevealInFolder?: (item: LibraryItem) => void;
  style?: CSSProperties;
  className?: string;
}) {
  const audio = isAudioItem(item);
  const text = isTextItem(item);
  const actionsCols = onRevealInFolder ? 88 : 64;

  return (
    <div
      data-testid={`file-row-${item.id}`}
      data-highlight={highlighted ? 'true' : undefined}
      className={cn(
        'group grid gap-2 items-center px-3 border-b border-border/40',
        playing || highlighted ? 'bg-row-active' : 'hover:bg-row-hover',
        className,
      )}
      style={{
        ...style,
        gridTemplateColumns: `minmax(0,1fr) 72px 56px 72px ${actionsCols}px`,
      }}
      onDoubleClick={() => {
        if (audio) onPlay(item);
        else if (text) onOpenText(item);
      }}
    >
      <button
        type="button"
        className="flex min-w-0 items-center gap-2 text-left"
        onClick={() => {
          if (audio) onPlay(item);
          else if (text) onOpenText(item);
        }}
      >
        <FileTypeIcon type={item.type} />
        <HighlightedName name={item.filename} query={highlightQuery} />
        {'score' in item && typeof item.score === 'number' ? (
          <span className="shrink-0 font-mono text-[10px] text-text-muted tabular-nums">
            {Math.round(Math.min(1, Math.max(0, item.score)) * 100)}%
          </span>
        ) : null}
      </button>
      <span className="font-mono text-[11px] text-text-muted text-right tabular-nums">
        {formatDurationSafe('durationMs' in item ? item.durationMs : undefined)}
      </span>
      <span className="font-mono text-[11px] text-text-muted text-right">
        {extensionLabel(item)}
      </span>
      <span className="font-mono text-[11px] text-text-muted text-right tabular-nums">
        {formatSizeSafe('size' in item ? item.size : undefined)}
      </span>
      <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
        {onRevealInFolder ? (
          <Tooltip content={ru.showInFolder}>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`${ru.showInFolder}: ${item.filename}`}
              onClick={(e) => {
                e.stopPropagation();
                onRevealInFolder(item);
              }}
            >
              <FolderOpen className="h-3.5 w-3.5" />
            </Button>
          </Tooltip>
        ) : null}
        {audio ? (
          <Tooltip content={ru.play}>
            <Button
              size="icon"
              variant="ghost"
              aria-label={`${ru.play} ${item.filename}`}
              onClick={() => onPlay(item)}
            >
              <Play className="h-3.5 w-3.5" />
            </Button>
          </Tooltip>
        ) : null}
        <Tooltip content={ru.download}>
          <a
            href={downloadUrl(item.id)}
            download
            aria-label={`${ru.download} ${item.filename}`}
            className="inline-flex h-7 w-7 items-center justify-center text-text-muted hover:text-text hover:bg-row-hover focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-3.5 w-3.5" />
          </a>
        </Tooltip>
      </div>
    </div>
  );
}

export function FileList({
  items,
  highlightQuery,
  loading,
  error,
  onRetry,
  onOpenText,
  onRevealInFolder,
  emptyTitle = ru.noFiles,
  emptyDescription = ru.folderEmpty,
  hasMore,
  onLoadMore,
  loadingMore,
  highlightFileId,
}: FileListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const player = usePlayer();
  const actionsCols = onRevealInFolder ? 88 : 64;

  const rowCount = items.length + (hasMore ? 1 : 0);
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 12,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastItem = virtualItems[virtualItems.length - 1];

  useEffect(() => {
    if (!hasMore || !onLoadMore || loadingMore) return;
    if (lastItem && lastItem.index >= items.length - 1) {
      onLoadMore();
    }
  }, [hasMore, onLoadMore, loadingMore, lastItem, items.length]);

  useEffect(() => {
    if (!highlightFileId) return;
    const idx = items.findIndex((i) => i.id === highlightFileId);
    if (idx < 0) return;
    virtualizer.scrollToIndex(idx, { align: 'center' });
  }, [highlightFileId, items, virtualizer]);

  const usePlain = virtualItems.length === 0 && items.length > 0 && items.length <= 200;

  if (loading) {
    return (
      <div className="space-y-1 p-2" data-testid="file-list-loading">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return <ErrorState title={ru.loadFailed} message={error} onRetry={onRetry} />;
  }

  if (items.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  const play = (item: LibraryItem) => player.playTrack(item, items);

  const headerStyle = {
    gridTemplateColumns: `minmax(0,1fr) 72px 56px 72px ${actionsCols}px`,
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="file-list">
      <div
        className="grid gap-2 border-b border-border px-3 py-1.5 text-[10px] uppercase tracking-wider text-text-muted"
        style={headerStyle}
      >
        <span>{ru.name}</span>
        <span className="text-right">{ru.duration}</span>
        <span className="text-right">{ru.format}</span>
        <span className="text-right">{ru.size}</span>
        <span className="text-right"> </span>
      </div>
      <div ref={parentRef} className="min-h-0 flex-1 overflow-auto" style={{ minHeight: 120 }}>
        {usePlain ? (
          <div>
            {items.map((item) => (
              <FileRow
                key={item.id}
                item={item}
                highlightQuery={highlightQuery}
                playing={player.track?.id === item.id}
                highlighted={highlightFileId === item.id}
                onOpenText={onOpenText}
                onPlay={play}
                onRevealInFolder={onRevealInFolder}
                style={{ height: 36 }}
              />
            ))}
            {hasMore ? (
              <div className="flex h-9 items-center justify-center">
                <button
                  type="button"
                  className="text-xs text-accent hover:underline disabled:opacity-50"
                  disabled={loadingMore}
                  onClick={onLoadMore}
                >
                  {loadingMore ? ru.loading : ru.loadMore}
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
            {virtualItems.map((virtualRow) => {
              if (virtualRow.index >= items.length) {
                return (
                  <div
                    key="load-more"
                    className="absolute left-0 top-0 w-full flex items-center justify-center"
                    style={{
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <button
                      type="button"
                      className="text-xs text-accent hover:underline disabled:opacity-50"
                      disabled={loadingMore}
                      onClick={onLoadMore}
                    >
                      {loadingMore ? ru.loading : ru.loadMore}
                    </button>
                  </div>
                );
              }

              const item = items[virtualRow.index]!;
              return (
                <FileRow
                  key={item.id}
                  item={item}
                  highlightQuery={highlightQuery}
                  playing={player.track?.id === item.id}
                  highlighted={highlightFileId === item.id}
                  onOpenText={onOpenText}
                  onPlay={play}
                  onRevealInFolder={onRevealInFolder}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
